/**
 * Ввод и показ даты со временем для органайзера (эпик 9, стори 9.3 и 9.4).
 *
 * <p>Ввод разный по платформам, а формат один. В вебе поле — настоящий `<input type="date">`,
 * и календарь показывает браузер (`DateTimeField.web.tsx`); на телефоне — ручной ввод, потому
 * что системный `DateTimePicker` тянет нативную часть, то есть пересборку через EAS ради формы
 * из двух строк. Быстрые кнопки («Завтра 09:00») закрывают там большинство случаев, ради
 * которых календарь и открывают.
 *
 * <p>Формат ДД.ММ.ГГГГ — тот, что стоит в подсказках всех четырёх локалей. Разбор нарочно
 * строгий: «31.02.2026» отвергается, а не превращается молча в 3 марта, как сделал бы
 * `new Date`.
 */

/** Часовой пояс устройства (IANA). Пусто не возвращает: сервер ждёт непустую строку. */
export function deviceTimeZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof zone === 'string' && zone.length > 0) return zone;
  } catch {
    /* Intl без данных о зонах — редкость, но встречается на старых Android-сборках */
  }
  return 'UTC';
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function formatDateInput(d: Date): string {
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export function formatTimeInput(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * Разбор пары полей в местный момент времени.
 *
 * @returns `null`, если дата или время не разобрались либо такого дня не существует
 */
export function parseLocalDateTime(dateText: string, timeText: string): Date | null {
  const dateMatch = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/.exec(dateText.trim());
  const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(timeText.trim());
  if (!dateMatch || !timeMatch) return null;
  const day = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const year = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (hour > 23 || minute > 59) return null;
  const d = new Date(year, month - 1, day, hour, minute, 0, 0);
  // Проверка «дата пережила конструктор»: 31.02 превращается в 3 марта молча, и без этой
  // сверки человек назначил бы напоминание на день, которого не выбирал.
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

/** Показ момента в местном времени: «29.08.2026, 18:00». */
export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${formatDateInput(d)}, ${formatTimeInput(d)}`;
}

/** Ближайшие «круглые» варианты — ими закрывается почти всё, ради чего открывают календарь. */
export function quickPickToday18(now: Date = new Date()): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 18, 0, 0, 0);
  // Если восемнадцать уже прошло, «сегодня 18:00» — это вчера; сдвигаем на сутки, иначе
  // кнопка предлагала бы заведомо отвергаемое сервером время.
  if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1);
  return d;
}

export function quickPickTomorrow9(now: Date = new Date()): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
}

export function quickPickInAnHour(now: Date = new Date()): Date {
  return new Date(now.getTime() + 60 * 60 * 1000);
}

/**
 * Перевод между тем, что печатает человек, и тем, чем оперирует браузер.
 *
 * <p>`<input type="date">` работает только в ISO (`гггг-мм-дд`), а во всём остальном проекте
 * дата — «ДД.ММ.ГГГГ»: так она подписана в подсказках всех четырёх локалей и так её разбирает
 * {@link parseLocalDateTime}. Перевод живёт здесь, а не в веб-компоненте, ровно поэтому: он
 * относится к формату даты, а не к устройству поля ввода, — и здесь он под тестами.
 *
 * @returns пустую строку на неразобранном вводе: `<input type="date">` отвергает всё, что не
 *     ISO, и подсунуть ему полуфабрикат вроде «01.02.19» значит получить пустое поле молча
 */
export function displayDateToIso(display: string): string {
  const m = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/.exec(display.trim());
  return m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
}

/** Обратный перевод; пустое значение — это очищенное поле, а не ошибка. */
export function isoDateToDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

/**
 * Зона напоминания рядом с временем — только когда она отличается от здешней.
 *
 * <p>Подпись «задано по Europe/Belgrade» человеку, который в Белграде и сидит, — это шум.
 * Она нужна ровно в одном случае: он назначил время дома и уехал, и теперь местные часы
 * показывают не то, что он вводил.
 */
export function foreignZoneLabel(reminderZone: string): string | null {
  const here = deviceTimeZone();
  return reminderZone && reminderZone !== here ? reminderZone : null;
}
